import './chat-content.css'
import FaceIcon from '../images/face-icon.png'
import DocumentSearch from '../images/doc-search.png'
import Enter from '../images/enter.png'
import Mic from '../images/mic.png'
import { useRef, useEffect, useState } from 'react'
import { authFetch, getDomain, postData } from '../utils/apis/api'
import { Toast, ToastContainer, Modal } from 'react-bootstrap'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism'
import Skeleton from 'react-loading-skeleton'
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react'

import 'react-loading-skeleton/dist/skeleton.css'
import PolicyDialog from '../components/policy'

const ChatContent = ({ chatContentState }) => {
  const [token, setToken] = useState(null)
  const [isAudioRoom, setIsAudioRoom] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [chatHistory, setChatHistory] = useState([])
  const [showChat, setShowChat] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
  const [voicePromptHint, setVoicePromptHint] = useState('')
  const chatRef = useRef(null)
  const textareaRef = useRef(null)
  const [showMicModal, setShowMicModal] = useState(false)

  const [imageSrc, setImageSrc] = useState(null)

  const handleShowToast = (message) => {
    setToastMessage(message)
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
    }, 3000)
  }

  function determineLiveKitServerUrl() {
    const url = window.location.href

    if (url.includes('localhost')) return 'wss://tranquilla-c1gx9h6q.livekit.cloud'
    if (url.includes('tranquilla.nearlyhuman.ai')) return 'wss://tranquilla-c1gx9h6q.livekit.cloud'
    if (url.includes('tranquilla.mresult.com')) return 'wss://tranquilla-voice-dev-8ss7lzoz.livekit.cloud'
    if (url.includes('tranquilla-dev.mresult.com')) return 'wss://token-server-voice-xl4kpdnf.livekit.cloud'
    if (url.includes('tranquilla-dev.nearlyhuman.ai/')) return 'wss://tranquilla-dev-xlg7p3hk.livekit.cloud'
    return ''
  }

  useEffect(() => {
    const fetchToken = async () => {
      const userId = sessionStorage.getItem('user_id') || 'guest_' + Date.now()

      try {
        const apiDomain = getDomain()

        const roomName = `${userId}${new Date()}`
        const res = await authFetch(
          `${apiDomain}get_token?room_name=${encodeURIComponent(roomName)}&participant_name=${encodeURIComponent(userId)}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            },
          }
        )

        if (!res.ok) {
          let errorMessage = 'Unable to start voice chat.'

          try {
            const errorData = await res.json()
            errorMessage = errorData.detail || errorMessage
          } catch {
            errorMessage = res.statusText || errorMessage
          }

          if (res.status === 403) {
            handleShowToast(errorMessage)
            setShowMicModal(false)
            return
          }

          throw new Error(errorMessage)
        }

        const data = await res.json()

        if (data) {
          setToken(data)
          setIsAudioRoom(true)
        }
      } catch (error) {
        console.error('Error fetching token:', error)
        handleShowToast(error.message || 'Unable to start voice chat.')
        setShowMicModal(false)
      }
    }

    if (showMicModal) {
      fetchToken()
    } else {
      setIsAudioRoom(false)
      setToken(null)
    }
  }, [showMicModal])

  useEffect(() => {
    const fetchIcon = async () => {
      if (sessionStorage.getItem('userType') !== 'admin') {
        try {
          const data = {
            agent_name: sessionStorage.getItem('agentName'),
            type_: 'icon',
            filename: `${sessionStorage.getItem('agentName')}.png`,
          }

          const response = await postData('/fetch-icon', data)

          if (response) {
            setImageSrc(`data:image/png;base64,${response}`)
          } else {
            setImageSrc(FaceIcon)
          }
        } catch (error) {
          setImageSrc(FaceIcon)
        }
      }
    }

    fetchIcon()
  }, [])


  const cleanMarkdown = (text) => {
    return text
      .replace(/\r\n|\r/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/(\d+)\s+\.\s+/g, '$1. ')
      .replace(/([^\n])\s+(\d+\.\s+\*\*)/g, '$1\n\n$2')
      .replace(/([^\n])\s+(\d+\.\s+[A-Z][^:\n]{2,80}:)/g, '$1\n\n$2')
      .replace(/([.!?])\s+(\d+\.\s+)/g, '$1\n\n$2')
      .replace(/([^\n])\n(?=[^\n])/g, '$1 ')
      .replace(/(\s*\n){3,}/g, '\n\n')
      .replace(/[ ]{2,}/g, ' ')
      .replace(/\s+([.,;:!?])/g, '$1')
      .replace(/(\(|\[)\s+/g, '$1')
      .replace(/\s+(\)|\])/g, '$1')
      .replace(/\*\*\s*(.*?)\s*\*\*/g, (_, t) => `**${t.trim()}**`)
      .trim()
  }

  const handleSave = async () => {
    const query = userInput.trim()
    if (!query) return

    setUserInput('')
    setShowChat(false)
    setLoading(false)

    const newChat = { query, response: '' }
    setChatHistory((prev) => [...prev, newChat])
    scrollToBottom()

    const payload = {
      user_message: query,
      thread_id: sessionStorage.getItem('thread_id') || '',
      username: sessionStorage.getItem('user_id'),
    }

    try {
      const response = await authFetch(
        sessionStorage.getItem('baseURL') + 'chat-streaming',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} - ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No readable stream found')

      const decoder = new TextDecoder()
      let done = false
      let accumulatedMarkdown = ''
      let latestRespId = null

      while (!done) {
        const { value, done: readerDone } = await reader.read()
        done = readerDone

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk
          .split('\n#\n')
          .filter((line) => line.startsWith('data: '))

        for (const line of lines) {
          const data = line.replace(/^data: /, '')

          if (data.startsWith('id=resp_')) {
            latestRespId = data.replace('id=', '').trim()
            continue
          }

          if (data === '') {
            accumulatedMarkdown += '\n\n'
          }
          if (data === '**\n') {
            accumulatedMarkdown += '\n\n\n '
          }

          accumulatedMarkdown += data

          setChatHistory((prev) => {
            const updated = [...prev]
            const lastIndex = updated.length - 1
            updated[lastIndex] = {
              ...updated[lastIndex],
              response: cleanMarkdown(accumulatedMarkdown),
            }
            return updated
          })

          scrollToBottom()
        }
      }

      if (latestRespId) {
        sessionStorage.setItem('thread_id', latestRespId)
      }
    } catch (error) {
      console.error('Streaming error:', error)
      setChatHistory((prev) =>
        prev.map((chat, index) =>
          index === prev.length - 1
            ? {
              ...chat,
              response: '⚠️ Error retrieving response. Please try again.',
            }
            : chat
        )
      )
      handleShowToast('Error receiving response')
    } finally {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = 'auto'
      }
    }
  }

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }

  const handleScroll = () => {
    const chatDiv = chatRef.current
    if (chatDiv) {
      const isBottom =
        chatDiv.scrollHeight - chatDiv.scrollTop === chatDiv.clientHeight
      setIsUserScrolledUp(!isBottom)
    }
  }

  const scrollToBottom = () => {
    const chatDiv = chatRef.current
    if (chatDiv) {
      chatDiv.scrollTop = chatDiv.scrollHeight
      setIsUserScrolledUp(false)
    }
  }

  useEffect(() => {
    const pendingPrompt = sessionStorage.getItem('pendingChatPrompt')
    const shouldOpenVoiceChat = sessionStorage.getItem('openVoiceChat') === 'true'
    const pendingVoiceHint = sessionStorage.getItem('voicePromptHint') || ''

    if (!pendingPrompt && !shouldOpenVoiceChat) return

    sessionStorage.removeItem('pendingChatPrompt')
    sessionStorage.removeItem('openVoiceChat')
    sessionStorage.removeItem('voicePromptHint')

    if (pendingPrompt) {
      setUserInput(pendingPrompt)
    }

    if (pendingVoiceHint) {
      setVoicePromptHint(pendingVoiceHint)
    }

    setShowChat(true)

    if (shouldOpenVoiceChat) {
      setShowMicModal(true)
    }

    setTimeout(() => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    }, 0)
  }, [])

  useEffect(() => {
    if (chatRef.current && !isUserScrolledUp) {
      scrollToBottom()
    }
  }, [chatHistory, isUserScrolledUp])

  useEffect(() => {
    if (chatContentState) {
      setShowChat(true)
      setChatHistory([])
    }
  }, [chatContentState])

  const renderers = {
    code({ inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '')
      return !inline && match ? (
        <SyntaxHighlighter
          style={dracula}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      )
    },
    h1: ({ children }) => <h1>{children}</h1>,
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    h4: ({ children }) => <h4>{children}</h4>,
    h5: ({ children }) => <h5>{children}</h5>,
    h6: ({ children }) => <h6>{children}</h6>,
    p: ({ children }) => <p>{children}</p>,
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    ul: ({ children }) => <ul>{children}</ul>,
    ol: ({ children }) => <ol>{children}</ol>,
    li: ({ children }) => <li>{children}</li>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
    img: ({ src, alt }) => (
      <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />
    ),
    video: ({ src }) => (
      <video src={src} controls style={{ maxWidth: '100%' }}>
        Your browser does not support the video tag.
      </video>
    ),
  }

  return (
    <div className="chat-content-main">

      {showChat ? (
        <div className="chat-layout" style={{ overflowY: 'auto', position: 'relative' }}>
          {sessionStorage.getItem('userType') !== 'admin' && (
            <img src={imageSrc || FaceIcon} alt="GPT icon" className="gpt-icon" />
          )}
          {sessionStorage.getItem('userType') === 'admin' && (
            <img src={FaceIcon} alt="faceicon" className="gpt-icon" />
          )}
          <div className="d-flex gap-5 cards-layout flex-column flex-lg-row">
            <div className="custom-card">
              <div>
                <img src={DocumentSearch} alt="document-search" width="30" />
              </div>
              <div className="mt-4">
                Type hello and ask for main menu options to start.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="Chats"
          ref={chatRef}
          onScroll={handleScroll}
          style={{ overflowY: 'auto', position: 'relative' }}
        >
          {chatHistory.map((chat, index) => (
            <div key={index} className="chat-block">
              <div className="UserQuery">
                <div className="user-text">{chat.query}</div>
              </div>
              <div className="GptResponse">
                {sessionStorage.getItem('userType') !== 'admin' && (
                  <img
                    src={imageSrc || FaceIcon}
                    alt="GPT icon"
                    className="gpt-icon-chat"
                  />
                )}
                {sessionStorage.getItem('userType') === 'admin' && (
                  <img src={FaceIcon} alt="faceicon" className="gpt-icon-chat" />
                )}
                <div
                  className="chat-response ps-2 col-10"
                  style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                >
                  {loading && index === chatHistory.length - 1 ? (
                    <div style={{ width: '100%' }}>
                      <Skeleton count={3} />
                    </div>
                  ) : (
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={renderers}
                    >
                      {cleanMarkdown(chat.response)}
                    </Markdown>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="input-container">
        <div className="input-inner-container">
          <textarea
            ref={textareaRef}
            value={userInput}
            onChange={(event) => {
              setUserInput(event.target.value)
              adjustTextareaHeight()
            }}
            className="InputStyles"
            placeholder="Type your message here..."
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSave()
              }
            }}
            style={{ resize: 'none', overflow: 'hidden' }}
          />

          <span
            onClick={() => setShowMicModal(true)}
            role="button"
            className="mic-icon"
          >
            <img src={Mic} width="25" alt="mic" />
          </span>

          <span onClick={handleSave} role="button" className="enter-icon">
            <img src={Enter} width="35" alt="enter" />
          </span>
        </div>
      </div>

      <PolicyDialog />

      <div>
        <Modal show={showMicModal} onHide={() => setShowMicModal(false)} centered dialogClassName="voice-chat-modal">
          <Modal.Header closeButton>
            <Modal.Title>Voice Chat</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="voice-modal-body">
              <div className="voice-mic-ring">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="44"
                  height="44"
                  fill="currentColor"
                  className="bi bi-mic"
                  viewBox="0 0 16 16"
                >
                  <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5" />
                  <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3" />
                </svg>
              </div>
              {voicePromptHint && (
                <p className="voice-prompt-hint">
                  {voicePromptHint}
                </p>
              )}
            </div>
          </Modal.Body>
        </Modal>

      </div>

      {isAudioRoom && !token && (
        <div className="text-center my-3">Fetching voice token...</div>
      )}

      {isAudioRoom && token && (
        <LiveKitRoom
          serverUrl={determineLiveKitServerUrl()}
          token={token}
          connect={true}
          audio={true}
          video={false}
          style={{ height: 0, width: 0 }}
        >
          <RoomAudioRenderer />
        </LiveKitRoom>
      )}

      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)}>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  )
}

export default ChatContent
